import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogger } from '../../context/useLogger';
import Box from 'carbon-react/esm/components/box';
import Button from 'carbon-react/esm/components/button/__next__';
import Typography from 'carbon-react/esm/components/typography';
import Icon, { IconType } from 'carbon-react/esm/components/icon';
import { ENV } from '../../config/env';
import { AuthenticationError } from '../../types/AuthTypes';

export interface ErrorFallbackProps {
  error: unknown;
  resetErrorBoundary: () => void;
  additionalActions?: ReactNode;
}

interface ErrorFallbackBaseProps extends ErrorFallbackProps {
  title?: string;
  message?: string;
  iconType?: IconType;
  titleColor?: string;
  showRetry?: boolean;
  showHome?: boolean;
  showReload?: boolean;
  showLogout?: boolean;
  showDebugDetails?: boolean;
  showGitSha?: boolean;
  compact?: boolean;
}

export const ErrorFallbackBase = ({
  error,
  resetErrorBoundary,
  additionalActions,
  title = "Oops ! Une erreur s'est produite",
  message = "Nous sommes désolés, une erreur inattendue s'est produite lors du chargement de vos informations. Nos équipes ont été notifiées et travaillent à résoudre le problème.",
  iconType = 'error',
  titleColor = 'var(--colorsSemanticNegative500)',
  showRetry = true,
  showHome = true,
  showReload = false,
  showLogout = false,
  showDebugDetails = false,
  showGitSha = false,
  compact = false,
}: ErrorFallbackBaseProps) => {
  const navigate = useNavigate();
  const logger = useLogger();
  const gitSha = import.meta.env.VITE_GIT_SHA;

  const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
  const errorStack = error instanceof Error ? error.stack : undefined;

  useEffect(() => {
    logger.error('Unexpected error occurred:', { message: errorMessage, stack: errorStack });

    if (error instanceof AuthenticationError) {
      void navigate('/login');
    }
    if (errorMessage.includes('Login required')) {
      void navigate('/login');
    }
  }, [error, errorMessage, errorStack, navigate, logger]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      {...(!compact && { justifyContent: 'center', height: '700px' })}
      p={20}
    >
      <Box
        maxWidth="600px"
        p={40}
        bg="white"
        {...(!compact && { borderRadius: 'borderRadius100', boxShadow: 'boxShadow100' })}
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={3}
        role="alert"
      >
        <span style={{ color: titleColor }}>
          <Icon type={iconType} size="large" />
        </span>

        <Typography variant={compact ? 'h2' : 'h1'} textAlign="center">
          {title}
        </Typography>

        <Typography variant="p" textAlign="center">
          {message}
        </Typography>

        {showDebugDetails && (process.env.NODE_ENV === 'development' || ENV.DEBUG_MODE) && (
          <Box width="100%" p={15} bg="#f8f9fa" borderRadius="borderRadius050">
            <details>
              <summary style={{ cursor: 'pointer', marginBottom: '10px' }}>
                <Typography variant="b">Détails de l&apos;erreur (développement)</Typography>
              </summary>
              <Box as="pre" overflow="auto" mt="10px">
                <Typography as="code" variant="small" size="M" whiteSpace="pre-wrap">
                  {errorMessage}
                  {'\n\n'}
                  {errorStack}
                </Typography>
              </Box>
            </details>
          </Box>
        )}

        <Box display="flex" flexWrap="wrap" justifyContent="center" gap={2} mt={2}>
          {showRetry && (
            <Button onClick={resetErrorBoundary} variantType="primary" size="large">
              Réessayer
            </Button>
          )}

          {additionalActions}

          {showHome && (
            <Button
              onClick={() => (window.location.href = '/')}
              variantType="secondary"
              size="large"
            >
              Retour à l&apos;accueil
            </Button>
          )}

          {showReload && (
            <Button onClick={() => window.location.reload()} variantType="secondary" size="large">
              Recharger la page
            </Button>
          )}

          {showLogout && (
            <Button
              onClick={() => (window.location.href = '/logout')}
              variantType="tertiary"
              size="large"
            >
              Se déconnecter
            </Button>
          )}
        </Box>

        {showGitSha && gitSha && (
          <Box display="flex" justifyContent="flex-end" width="100%">
            <Typography as="code" variant="small" size="M" whiteSpace="pre-wrap">
              {`Git SHA: ${gitSha}`}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
